import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  listDashboard,
  saveContact,
  deleteContact,
  saveTask,
  deleteTask,
  updateDraft,
  deleteDraft,
  logActivity,
  generateEmailDraft,
  generateSocialPosts,
  generateDailyPlan,
  sendDraftEmail,

} from "@/lib/dashboard.functions";
import {
  listBusinessData,
  importQuickbooksRows,
  deleteFinanceImport,
  getSearchConsoleSites,
  getSiteTraffic,
  getLinkedInIdentity,
  publishToLinkedIn,
  saveSocialMetrics,
} from "@/lib/business.functions";
import { ContactDialog, type ContactFormValues } from "@/components/dashboard/ContactDialog";
import { TodayPanel } from "@/components/dashboard/TodayPanel";
import { PipelinePanel } from "@/components/dashboard/PipelinePanel";
import { DraftsPanel } from "@/components/dashboard/DraftsPanel";
import { OverviewPanel } from "@/components/dashboard/OverviewPanel";
import { RevenuePanel } from "@/components/dashboard/RevenuePanel";
import { TrafficPanel, type TrafficData } from "@/components/dashboard/TrafficPanel";
import { LinkedInPanel } from "@/components/dashboard/LinkedInPanel";
import {
  listGrowthData,
  saveProject,
  deleteProject,
  rotateProjectKey,
  updateLead,
  deleteLead,
  promoteLead,
  saveProposal,
  deleteProposal,
  saveRetainer,
  deleteRetainer,
  saveDigestSettings,
  sendDigestNow,
} from "@/lib/growth.functions";
import { ProjectsPanel, type ProjectFormValues } from "@/components/dashboard/ProjectsPanel";
import { TrackingPanel } from "@/components/dashboard/TrackingPanel";
import { IntegrationsPanel, type IntegrationStatus } from "@/components/dashboard/IntegrationsPanel";
import { getIntegrationStatus, rotateIngestKey, testHoneybookIngest } from "@/lib/integrations.functions";
import { LeadsPanel } from "@/components/dashboard/LeadsPanel";
import { MoneyPanel } from "@/components/dashboard/MoneyPanel";
import type { ParsedFinanceRow } from "@/lib/quickbooks-parse";
import type { ContactRow } from "@/components/dashboard/types";


export const Route = createFileRoute("/dashboard")({
  head: () => ({
    meta: [
      { title: "Business Development Workspace, Square Migration" },
      {
        name: "description",
        content: "Private workspace to manage the Square Migration pipeline, outbound email drafts, and social posts.",
      },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Business Development Workspace" },
      { property: "og:description", content: "Private pipeline, outbound email, and social post workspace." },
    ],
  }),
  component: DashboardPage,
});

function DashboardPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [sessionState, setSessionState] = useState<"loading" | "in" | "out">("loading");
  const [email, setEmail] = useState<string | null>(null);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<ContactRow | null>(null);
  const [tab, setTab] = useState("overview");
  const [site, setSite] = useState("https://squaremigration.com/");
  const [trafficDays, setTrafficDays] = useState(28);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSessionState(data.session ? "in" : "out");
      setEmail(data.session?.user.email ?? null);
      if (!data.session) navigate({ to: "/auth" });
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      setSessionState(session ? "in" : "out");
      setEmail(session?.user.email ?? null);
    });
    return () => sub.subscription.unsubscribe();
  }, [navigate]);

  const fetchDashboard = useServerFn(listDashboard);
  const dashboard = useQuery({
    queryKey: ["dashboard"],
    queryFn: () => fetchDashboard(),
    enabled: sessionState === "in",
  });

  const fetchBusiness = useServerFn(listBusinessData);
  const business = useQuery({
    queryKey: ["business"],
    queryFn: () => fetchBusiness(),
    enabled: sessionState === "in",
  });

  const fetchSites = useServerFn(getSearchConsoleSites);
  const sitesQuery = useQuery({
    queryKey: ["search-console-sites"],
    queryFn: () => fetchSites(),
    enabled: sessionState === "in",
    staleTime: 60 * 60 * 1000,
  });

  // If the default property isn't verified on this account, fall back to the first one.
  useEffect(() => {
    const sites = sitesQuery.data as string[] | undefined;
    if (sites?.length && !sites.includes(site)) setSite(sites[0]!);
  }, [sitesQuery.data, site]);


  const fetchTraffic = useServerFn(getSiteTraffic);
  const trafficQuery = useQuery({
    queryKey: ["traffic", site, trafficDays],
    queryFn: () => fetchTraffic({ data: { site_url: site, days: trafficDays } }),
    enabled: sessionState === "in" && Boolean(site),
    staleTime: 15 * 60 * 1000,
  });

  const fetchIdentity = useServerFn(getLinkedInIdentity);
  const identityQuery = useQuery({
    queryKey: ["linkedin-identity"],
    queryFn: () => fetchIdentity(),
    enabled: sessionState === "in",
    staleTime: 60 * 60 * 1000,
  });

  const fetchGrowth = useServerFn(listGrowthData);
  const growth = useQuery({
    queryKey: ["growth"],
    queryFn: () => fetchGrowth(),
    enabled: sessionState === "in",
  });

  const fetchIntegrations = useServerFn(getIntegrationStatus);
  const integrations = useQuery({
    queryKey: ["integrations"],
    queryFn: () => fetchIntegrations(),
    enabled: sessionState === "in",
  });

  const rotateKeyIngestFn = useServerFn(rotateIngestKey);
  const rotateIngest = useMutation({
    mutationFn: () => rotateKeyIngestFn(),
    onSuccess: () => {
      toast.success("New ingest key generated, update your Zaps");
      queryClient.invalidateQueries({ queryKey: ["integrations"] });
    },
    onError: (error: unknown) =>
      toast.error(error instanceof Error ? error.message : "Something went wrong"),
  });

  const testHoneybookFn = useServerFn(testHoneybookIngest);
  const testIngest = useMutation({
    mutationFn: () => testHoneybookFn(),
    onSuccess: (result: { ok: boolean; table: string; id: string | null }) => {
      toast.success(
        `Test received, created ${result.table}${result.id ? ` (${result.id.slice(0, 8)})` : ""}`,
      );
      queryClient.invalidateQueries({ queryKey: ["integrations"] });
    },
    onError: (error: unknown) =>
      toast.error(error instanceof Error ? error.message : "Test failed"),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["dashboard"] });
  const invalidateBusiness = () => queryClient.invalidateQueries({ queryKey: ["business"] });
  const invalidateGrowth = () => queryClient.invalidateQueries({ queryKey: ["growth"] });
  const onError = (error: unknown) =>
    toast.error(error instanceof Error ? error.message : "Something went wrong");

  const importFn = useServerFn(importQuickbooksRows);
  const importMutation = useMutation({
    mutationFn: (input: {
      filename: string;
      report_kind: string;
      skipped_count: number;
      rows: ParsedFinanceRow[];
    }) => importFn({ data: input }),
    onSuccess: (result: { inserted: number; duplicates: number }) => {
      toast.success(
        `Imported ${result.inserted} rows${result.duplicates ? ` (${result.duplicates} already existed)` : ""}`,
      );
      invalidateBusiness();
    },
    onError,
  });

  const deleteImportFn = useServerFn(deleteFinanceImport);
  const removeImport = useMutation({
    mutationFn: (id: string) => deleteImportFn({ data: { id } }),
    onSuccess: () => {
      toast.success("Import removed");
      invalidateBusiness();
    },
    onError,
  });

  const publishFn = useServerFn(publishToLinkedIn);
  const publishMutation = useMutation({
    mutationFn: (text: string) => publishFn({ data: { text } }),
    onSuccess: () => {
      toast.success("Posted to LinkedIn");
      invalidateBusiness();
      invalidate();
    },
    onError,
  });

  const metricsFn = useServerFn(saveSocialMetrics);
  const metricsMutation = useMutation({
    mutationFn: (input: {
      id: string;
      impressions?: number | null;
      likes?: number | null;
      comments?: number | null;
      shares?: number | null;
    }) => metricsFn({ data: input }),
    onSuccess: invalidateBusiness,
    onError,
  });


  const saveProjectFn = useServerFn(saveProject);
  const projectMutation = useMutation({
    mutationFn: (values: ProjectFormValues) => saveProjectFn({ data: values }),
    onSuccess: () => {
      toast.success("Project saved");
      invalidateGrowth();
    },
    onError,
  });

  const deleteProjectFn = useServerFn(deleteProject);
  const removeProject = useMutation({
    mutationFn: (id: string) => deleteProjectFn({ data: { id } }),
    onSuccess: () => {
      toast.success("Project removed");
      invalidateGrowth();
    },
    onError,
  });

  const rotateKeyFn = useServerFn(rotateProjectKey);
  const rotateKey = useMutation({
    mutationFn: (id: string) => rotateKeyFn({ data: { id } }),
    onSuccess: () => {
      toast.success("New key generated, update the snippet on that site");
      invalidateGrowth();
    },
    onError,
  });

  const updateLeadFn = useServerFn(updateLead);
  const leadMutation = useMutation({
    mutationFn: (input: { id: string; status?: string; score?: string }) =>
      updateLeadFn({ data: input as never }),
    onSuccess: invalidateGrowth,
    onError,
  });

  const deleteLeadFn = useServerFn(deleteLead);
  const removeLead = useMutation({
    mutationFn: (id: string) => deleteLeadFn({ data: { id } }),
    onSuccess: invalidateGrowth,
    onError,
  });

  const promoteLeadFn = useServerFn(promoteLead);
  const promote = useMutation({
    mutationFn: (id: string) => promoteLeadFn({ data: { id } }),
    onSuccess: (result: { draft: boolean }) => {
      toast.success(result?.draft ? "Added to pipeline with a reply draft" : "Added to pipeline");
      invalidateGrowth();
      invalidate();
    },
    onError,
  });

  const saveProposalFn = useServerFn(saveProposal);
  const proposalMutation = useMutation({
    mutationFn: (values: Record<string, unknown>) => saveProposalFn({ data: values as never }),
    onSuccess: invalidateGrowth,
    onError,
  });

  const deleteProposalFn = useServerFn(deleteProposal);
  const removeProposal = useMutation({
    mutationFn: (id: string) => deleteProposalFn({ data: { id } }),
    onSuccess: invalidateGrowth,
    onError,
  });

  const saveRetainerFn = useServerFn(saveRetainer);
  const retainerMutation = useMutation({
    mutationFn: (values: Record<string, unknown>) => saveRetainerFn({ data: values as never }),
    onSuccess: invalidateGrowth,
    onError,
  });

  const deleteRetainerFn = useServerFn(deleteRetainer);
  const removeRetainer = useMutation({
    mutationFn: (id: string) => deleteRetainerFn({ data: { id } }),
    onSuccess: invalidateGrowth,
    onError,
  });

  const digestSettingsFn = useServerFn(saveDigestSettings);
  const digestMutation = useMutation({
    mutationFn: (input: { enabled: boolean; recipient_email: string | null }) =>
      digestSettingsFn({ data: input }),
    onSuccess: invalidateGrowth,
    onError,
  });

  const sendDigestFn = useServerFn(sendDigestNow);
  const sendDigest = useMutation({
    mutationFn: () => sendDigestFn(),
    onSuccess: (result: { sent: boolean }) => {
      toast[result?.sent ? "success" : "error"](
        result?.sent ? "Digest sent" : "Could not send the digest, check the email setup",
      );
    },
    onError,
  });

  const saveContactFn = useServerFn(saveContact);
  const contactMutation = useMutation({
    mutationFn: (values: ContactFormValues) => saveContactFn({ data: values }),
    onSuccess: () => {
      toast.success("Contact saved");
      setContactDialogOpen(false);
      setEditingContact(null);
      invalidate();
    },
    onError,
  });

  const deleteContactFn = useServerFn(deleteContact);
  const removeContact = useMutation({
    mutationFn: (id: string) => deleteContactFn({ data: { id } }),
    onSuccess: () => {
      toast.success("Contact removed");
      invalidate();
    },
    onError,
  });

  const saveTaskFn = useServerFn(saveTask);
  const taskMutation = useMutation({
    mutationFn: (input: {
      id?: string;
      title: string;
      detail?: string | null;
      due_date?: string | null;
      done?: boolean;
    }) => saveTaskFn({ data: input }),
    onSuccess: invalidate,
    onError,
  });

  const deleteTaskFn = useServerFn(deleteTask);
  const removeTask = useMutation({
    mutationFn: (id: string) => deleteTaskFn({ data: { id } }),
    onSuccess: invalidate,
    onError,
  });

  const updateDraftFn = useServerFn(updateDraft);
  const draftMutation = useMutation({
    mutationFn: (input: {
      id: string;
      subject?: string | null;
      body?: string;
      status?: "draft" | "approved" | "sent" | "archived";
    }) => updateDraftFn({ data: input }),
    onSuccess: invalidate,
    onError,
  });

  const deleteDraftFn = useServerFn(deleteDraft);
  const removeDraft = useMutation({
    mutationFn: (id: string) => deleteDraftFn({ data: { id } }),
    onSuccess: invalidate,
    onError,
  });

  const sendDraftFn = useServerFn(sendDraftEmail);
  const sendDraft = useMutation({
    mutationFn: (input: { id: string; recipient_email?: string }) => sendDraftFn({ data: input }),
    onSuccess: (result: { sent: boolean }) => {
      if (result?.sent === false) {
        toast.error("That address is suppressed (bounced or unsubscribed), not sent.");
      } else {
        toast.success("Email sent.");
      }

      invalidate();
    },
    onError,
  });


  const logActivityFn = useServerFn(logActivity);
  const activityMutation = useMutation({
    mutationFn: (input: {
      contact_id?: string | null;
      type: "email" | "call" | "note" | "social" | "meeting";
      summary: string;
      detail?: string | null;
    }) => logActivityFn({ data: input }),
    onSuccess: () => {
      toast.success("Logged");
      invalidate();
    },
    onError,
  });

  const generateEmailFn = useServerFn(generateEmailDraft);
  const emailMutation = useMutation({
    mutationFn: (input: {
      contact_id: string;
      intent: "intro" | "follow_up" | "re_engage" | "proposal_nudge";
      angle?: string | null;
    }) => generateEmailFn({ data: input }),
    onSuccess: () => {
      toast.success("Email draft ready in the Outbound tab");
      invalidate();
    },
    onError,
  });

  const generateSocialFn = useServerFn(generateSocialPosts);
  const socialMutation = useMutation({
    mutationFn: (input: { topic?: string | null; count: number }) => generateSocialFn({ data: input }),
    onSuccess: () => {
      toast.success("Social drafts ready");
      invalidate();
    },
    onError,
  });

  const generatePlanFn = useServerFn(generateDailyPlan);
  const planMutation = useMutation({
    mutationFn: () => generatePlanFn(),
    onSuccess: () => {
      toast.success("Today's plan generated");
      invalidate();
    },
    onError,
  });

  if (sessionState !== "in") {
    return (
      <main className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-sm text-muted-foreground">Checking your session…</p>
      </main>
    );
  }

  const data = dashboard.data;
  const contacts = data?.contacts ?? [];
  const drafts = data?.drafts ?? [];
  const tasks = data?.tasks ?? [];
  const activities = data?.activities ?? [];
  const financeEntries = business.data?.entries ?? [];
  const financeImports = business.data?.imports ?? [];
  const socialPosts = business.data?.posts ?? [];
  const sites = sitesQuery.data?.sites ?? [];
  const traffic = trafficQuery.data as TrafficData | undefined;
  const projects = growth.data?.projects ?? [];
  const leads = growth.data?.leads ?? [];
  const proposals = growth.data?.proposals ?? [];
  const retainers = growth.data?.retainers ?? [];
  const projectStats = growth.data?.analytics.perProject ?? [];
  const newLeadCount = leads.filter((lead) => lead.status === "new").length;

  return (
    <main className="dashboard-surface min-h-screen bg-background">
      <Toaster />
      <header className="border-b border-border/40 px-6 py-5">
        <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-lg font-semibold text-foreground">Business Development Workspace</h1>
            <p className="text-xs text-muted-foreground">{email}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              className="rounded-full"
              onClick={() => {
                setEditingContact(null);
                setContactDialogOpen(true);
              }}
            >
              Add contact
            </Button>
            <Button
              variant="ghost"
              onClick={async () => {
                await supabase.auth.signOut();
                navigate({ to: "/auth" });
              }}
            >
              Sign out
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {dashboard.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading your pipeline…</p>
        ) : dashboard.isError ? (
          <p className="text-sm text-destructive">
            {dashboard.error instanceof Error ? dashboard.error.message : "Could not load your data."}
          </p>
        ) : (
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 rounded-xl border border-border/70 bg-secondary/60 p-1">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="today">Today</TabsTrigger>
              <TabsTrigger value="pipeline">Pipeline</TabsTrigger>
              <TabsTrigger value="revenue">Revenue</TabsTrigger>
              <TabsTrigger value="traffic">Traffic</TabsTrigger>
              <TabsTrigger value="linkedin">LinkedIn</TabsTrigger>
              <TabsTrigger value="projects">Client projects</TabsTrigger>
              <TabsTrigger value="tracking">Tracking</TabsTrigger>
              <TabsTrigger value="leads">
                Leads{newLeadCount ? ` (${newLeadCount})` : ""}
              </TabsTrigger>
              <TabsTrigger value="money">Proposals &amp; retainers</TabsTrigger>
              <TabsTrigger value="outbound">Outbound emails</TabsTrigger>
              <TabsTrigger value="social">Social posts</TabsTrigger>
              <TabsTrigger value="integrations">Integrations</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-6">
              <OverviewPanel
                entries={financeEntries}
                contacts={contacts}
                tasks={tasks}
                traffic={traffic}
                posts={socialPosts}
                onGoTo={setTab}
              />
            </TabsContent>

            <TabsContent value="revenue" className="mt-6 space-y-6">
              <RevenuePanel
                entries={financeEntries}
                imports={financeImports}
                importing={importMutation.isPending}
                onImport={(input) => importMutation.mutate(input)}
                onDeleteImport={(id) => removeImport.mutate(id)}
              />
            </TabsContent>

            <TabsContent value="traffic" className="mt-6">
              <TrafficPanel
                sites={sites.length > 0 ? sites : [site]}
                site={site}
                days={trafficDays}
                data={traffic}
                loading={trafficQuery.isFetching}
                error={
                  trafficQuery.error instanceof Error
                    ? trafficQuery.error.message
                    : (sitesQuery.data?.error ?? null)
                }
                onSiteChange={setSite}
                onDaysChange={setTrafficDays}
                onRefresh={() => trafficQuery.refetch()}
              />
            </TabsContent>

            <TabsContent value="linkedin" className="mt-6">
              <LinkedInPanel
                identity={identityQuery.data}
                posts={socialPosts}
                publishing={publishMutation.isPending}
                onPublish={(text) => publishMutation.mutate(text)}
                onSaveMetrics={(input) => metricsMutation.mutate(input)}
              />
            </TabsContent>



            <TabsContent value="projects" className="mt-6">
              <ProjectsPanel
                projects={projects}
                stats={projectStats}
                saving={projectMutation.isPending}
                onSave={(values) => projectMutation.mutate(values)}
                onDelete={(id) => removeProject.mutate(id)}
                onRotateKey={(id) => rotateKey.mutate(id)}
              />
            </TabsContent>

            <TabsContent value="tracking" className="mt-6">
              <TrackingPanel projects={projects} />
            </TabsContent>

            <TabsContent value="leads" className="mt-6">
              <LeadsPanel
                leads={leads}
                projects={projects}
                promoting={promote.isPending}
                onPromote={(id) => promote.mutate(id)}
                onUpdate={(input) => leadMutation.mutate(input)}
                onDelete={(id) => removeLead.mutate(id)}
              />
            </TabsContent>

            <TabsContent value="money" className="mt-6">
              <MoneyPanel
                proposals={proposals}
                retainers={retainers}
                contacts={contacts}
                digest={growth.data?.digest ?? null}
                saving={proposalMutation.isPending || retainerMutation.isPending}
                sendingDigest={sendDigest.isPending}
                onSaveProposal={(values) => proposalMutation.mutate(values as never)}
                onDeleteProposal={(id) => removeProposal.mutate(id)}
                onSaveRetainer={(values) => retainerMutation.mutate(values as never)}
                onDeleteRetainer={(id) => removeRetainer.mutate(id)}
                onSaveDigest={(values) => digestMutation.mutate(values)}
                onSendDigest={() => sendDigest.mutate()}
              />
            </TabsContent>

            <TabsContent value="today" className="mt-6">
              <TodayPanel
                tasks={tasks}
                contacts={contacts}
                activities={activities}
                drafts={drafts}
                generating={planMutation.isPending}
                onGeneratePlan={() => planMutation.mutate()}
                onToggleTask={(task) =>
                  taskMutation.mutate({ id: task.id, title: task.title, done: !task.done })
                }
                onAddTask={(title) => taskMutation.mutate({ title })}
                onDeleteTask={(id) => removeTask.mutate(id)}
              />
            </TabsContent>

            <TabsContent value="pipeline" className="mt-6">
              <PipelinePanel
                contacts={contacts}
                generating={emailMutation.isPending}
                onEdit={(contact) => {
                  setEditingContact(contact);
                  setContactDialogOpen(true);
                }}
                onDelete={(id) => removeContact.mutate(id)}
                onGenerateEmail={(input) => emailMutation.mutate(input)}
                onLogTouch={(contact) =>
                  activityMutation.mutate({
                    contact_id: contact.id,
                    type: "note",
                    summary: `Touched base with ${contact.name}`,
                  })
                }
              />
            </TabsContent>

            <TabsContent value="outbound" className="mt-6">
              <DraftsPanel
                kind="email"
                drafts={drafts.filter((draft) => draft.kind === "email")}
                contacts={contacts}
                onUpdate={(input) => draftMutation.mutate(input)}
                onDelete={(id) => removeDraft.mutate(id)}
                sending={sendDraft.isPending}
                onSend={(id, recipient) =>
                  sendDraft.mutate(recipient ? { id, recipient_email: recipient } : { id })
                }

              />
            </TabsContent>

            <TabsContent value="social" className="mt-6">
              <DraftsPanel
                kind="social"
                drafts={drafts.filter((draft) => draft.kind === "social")}
                contacts={contacts}
                generating={socialMutation.isPending}
                onGenerate={(topic) => socialMutation.mutate({ topic, count: 3 })}
                onUpdate={(input) => draftMutation.mutate(input)}
                onDelete={(id) => removeDraft.mutate(id)}
              />
            </TabsContent>
            <TabsContent value="integrations" className="mt-6 space-y-6">
              <IntegrationsPanel
                status={integrations.data as IntegrationStatus | undefined}
                loading={integrations.isLoading}
                rotating={rotateIngest.isPending}
                testing={testIngest.isPending}
                onRotate={() => rotateIngest.mutate()}
                onTest={() => testIngest.mutate()}
              />
            </TabsContent>
          </Tabs>
        )}
      </div>

      <ContactDialog
        open={contactDialogOpen}
        contact={editingContact}
        saving={contactMutation.isPending}
        onClose={() => {
          setContactDialogOpen(false);
          setEditingContact(null);
        }}
        onSave={(values) => contactMutation.mutate(values)}
      />
    </main>
  );
}
